/**
 * FatturatoPrevisionale — pagina di previsione fatturato aggregato per
 * periodo, cantiere, cliente, responsabile.
 *
 * Dati vengono dal Gantt (attività con importo + data_fatturazione_prevista o
 * fallback data_fine). Ogni voce mostra confronto vs emesso e incassato.
 */
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Receipt, Search, Filter, AlertTriangle, TrendingUp, TrendingDown } from "lucide-react";
import CashFlowForecast from "@/components/CashFlowForecast";
import WebdeskSyncButton from "@/components/WebdeskSyncButton";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

const STATO_FASE_META = {
  programmata: { l: "Programmata", c: "bg-slate-100 text-slate-700" },
  in_corso: { l: "In corso", c: "bg-amber-100 text-amber-800" },
  eseguita: { l: "Eseguita", c: "bg-emerald-100 text-emerald-800" },
  sospesa: { l: "Sospesa", c: "bg-slate-200 text-slate-700" },
  in_ritardo: { l: "In ritardo", c: "bg-rose-100 text-rose-800" },
  attesa_materiale: { l: "Attesa materiale", c: "bg-yellow-100 text-yellow-800" },
};

const STATO_RICH_META = {
  in_attesa_conferma: { l: "In attesa", c: "text-amber-700" },
  confermata: { l: "Confermata", c: "text-blue-700" },
  fatturata: { l: "Fatturata", c: "text-emerald-700" },
  rifiutata: { l: "Rifiutata", c: "text-rose-700" },
};

export default function FatturatoPrevisionale() {
  const { user } = useAuth();
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [cantiereId, setCantiereId] = useState("");
  const [cliente, setCliente] = useState("");
  const [statoFase, setStatoFase] = useState("");
  const [soloRitardo, setSoloRitardo] = useState(false);
  const [gran, setGran] = useState("mese");
  const [q, setQ] = useState("");

  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-min"],
    queryFn: () => api.get("/cantieri", { params: { limit: 500 } }).then((r) => r.data),
    enabled: isAdmin(user),
  });

  const { data: prev, isLoading } = useQuery({
    queryKey: ["previsionale", { anno, cantiereId, cliente, statoFase, soloRitardo }],
    queryFn: () =>
      api.get("/fatture/previsionale/list", {
        params: {
          anno,
          ...(cantiereId ? { cantiere_id: cantiereId } : {}),
          ...(cliente ? { cliente } : {}),
          ...(statoFase ? { stato_fase: statoFase } : {}),
          ...(soloRitardo ? { solo_ritardo: true } : {}),
        },
      }).then((r) => r.data),
    enabled: isAdmin(user),
  });

  const { data: breakdown } = useQuery({
    queryKey: ["previsionale-breakdown", { gran, anno, cantiereId }],
    queryFn: () =>
      api.get(`/fatture/breakdown/${gran}`, {
        params: { anno, ...(cantiereId ? { cantiere_id: cantiereId } : {}) },
      }).then((r) => r.data),
    enabled: isAdmin(user),
  });

  const clientiUnici = useMemo(() => {
    const set = new Set();
    cantieri.forEach((c) => {
      const cl = c.cliente_ragione_sociale || c.cliente;
      if (cl) set.add(cl);
    });
    return [...set].sort();
  }, [cantieri]);

  const voci = useMemo(() => {
    if (!prev?.voci) return [];
    if (!q.trim()) return prev.voci;
    const s = q.trim().toLowerCase();
    return prev.voci.filter((v) =>
      [v.fase_nome, v.cantiere_nome, v.cliente, v.responsabile].some((x) => (x || "").toLowerCase().includes(s))
    );
  }, [prev, q]);

  const tot = prev?.totali || {};

  if (!isAdmin(user)) return <div className="p-6 text-sm text-slate-600">Sezione riservata agli amministratori.</div>;

  return (
    <div className="space-y-5" data-testid="page-fatturato-previsionale">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Amministrazione</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
            <Receipt className="w-7 h-7 text-slate-500" /> Fatturato Previsionale
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Aggregazione automatica dalle date del Cronoprogramma. Le modifiche al Gantt aggiornano tutti i totali in real-time.
          </p>
        </div>
        <WebdeskSyncButton />
      </div>

      {/* Cash Flow Forecast — flussi in entrata previsti 15gg → 6 mesi */}
      <CashFlowForecast />

      {/* KPI totali */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="prev-kpi-row">        <KPI label="Previsto anno" value={eur(tot.previsto)} />
        <KPI label="Emesso" value={eur(tot.emesso)} highlight="blue" />
        <KPI label="Incassato" value={eur(tot.incassato)} highlight="emerald" />
        <KPI label="Da emettere" value={eur(tot.da_emettere)} highlight="amber" />
        <KPI label="Da incassare" value={eur(tot.da_incassare)} highlight="rose" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KPI label="% Realizzo" value={`${tot.pct_realizzo || 0}%`} icon={<TrendingUp className="w-3 h-3" />} />
        <KPI label="% Incasso" value={`${tot.pct_incasso || 0}%`} icon={<TrendingUp className="w-3 h-3" />} />
        <KPI label="Diff previsto/emesso" value={eur(tot.diff_previsto_emesso)} highlight={tot.diff_previsto_emesso > 0 ? "amber" : "emerald"} />
        <KPI label="Diff emesso/incassato" value={eur(tot.diff_emesso_incassato)} highlight={tot.diff_emesso_incassato > 0 ? "amber" : "emerald"} />
      </div>

      {/* Filtri */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-end gap-2 text-sm">
        <div className="flex flex-col">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Anno</span>
          <input type="number" value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="w-24 px-2 py-1.5 border border-slate-300 rounded-sm" data-testid="filtro-anno" />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Cantiere</span>
          <select value={cantiereId} onChange={(e) => setCantiereId(e.target.value)} className="min-w-[180px] px-2 py-1.5 border border-slate-300 rounded-sm bg-white" data-testid="filtro-cantiere">
            <option value="">— tutti —</option>
            {cantieri.map((c) => <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Cliente</span>
          <select value={cliente} onChange={(e) => setCliente(e.target.value)} className="min-w-[180px] px-2 py-1.5 border border-slate-300 rounded-sm bg-white" data-testid="filtro-cliente">
            <option value="">— tutti —</option>
            {clientiUnici.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Stato fase</span>
          <select value={statoFase} onChange={(e) => setStatoFase(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm bg-white" data-testid="filtro-stato-fase">
            <option value="">— tutti —</option>
            {Object.entries(STATO_FASE_META).map(([k, m]) => <option key={k} value={k}>{m.l}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer px-2 py-1.5 border border-slate-300 rounded-sm bg-white">
          <input type="checkbox" checked={soloRitardo} onChange={(e) => setSoloRitardo(e.target.checked)} data-testid="filtro-ritardo" />
          <AlertTriangle className="w-3 h-3 text-rose-500" /> Solo attività in ritardo
        </label>
        <div className="flex-1 min-w-[200px]">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Cerca</span>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Fase, cantiere, cliente…" className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded-sm" data-testid="filtro-cerca" />
          </div>
        </div>
      </div>

      {/* Breakdown periodo */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">Confronto previsionale/effettivo</div>
            <h3 className="font-display font-bold text-lg">Andamento per {gran}</h3>
          </div>
          <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden">
            {["mese", "trimestre", "anno"].map((g) => (
              <button key={g} onClick={() => setGran(g)} data-testid={`breakdown-${g}`} className={`px-3 py-1.5 text-xs font-semibold ${gran === g ? "bg-slate-800 text-white" : "bg-white hover:bg-slate-50"}`}>{g}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-widest">
              <tr>
                <th className="text-left p-2">Periodo</th>
                <th className="text-right p-2">Previsto</th>
                <th className="text-right p-2">Emesso</th>
                <th className="text-right p-2">Incassato</th>
                <th className="text-right p-2">Δ prev/emes</th>
                <th className="text-right p-2">% realizzo</th>
              </tr>
            </thead>
            <tbody>
              {(breakdown?.periodi || []).map((p) => (
                <tr key={p.periodo} className="border-t border-slate-100" data-testid={`bk-${p.periodo}`}>
                  <td className="p-2 font-mono font-bold">{p.periodo}</td>
                  <td className="p-2 text-right font-mono">{eur(p.previsto)}</td>
                  <td className="p-2 text-right font-mono">{eur(p.emesso)}</td>
                  <td className="p-2 text-right font-mono">{eur(p.incassato)}</td>
                  <td className={`p-2 text-right font-mono ${p.diff_previsto_emesso > 0 ? "text-amber-700" : "text-emerald-700"}`}>{eur(p.diff_previsto_emesso)}</td>
                  <td className="p-2 text-right font-mono">{p.pct_realizzo}%</td>
                </tr>
              ))}
              {(!breakdown?.periodi || breakdown.periodi.length === 0) && (
                <tr><td colSpan={6} className="p-4 text-center text-xs text-slate-400">Nessun dato per il periodo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lista voci */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-3 border-b border-slate-200">
          <div className="overline">Elenco voci previsionali</div>
          <h3 className="font-display font-bold text-lg">Dettaglio fasi ({voci.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-widest">
              <tr>
                <th className="text-left p-2">Cantiere</th>
                <th className="text-left p-2">Cliente</th>
                <th className="text-left p-2">Fase</th>
                <th className="text-left p-2">Data fatt.</th>
                <th className="text-right p-2">Previsto</th>
                <th className="text-right p-2">Emesso</th>
                <th className="text-right p-2">Incassato</th>
                <th className="text-center p-2">Stato fase</th>
                <th className="text-center p-2">Richiesta</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} className="p-4 text-center text-xs text-slate-400">Caricamento…</td></tr>}
              {!isLoading && voci.map((v) => (
                <tr key={v.attivita_id} className={`border-t border-slate-100 ${v.is_in_ritardo ? "bg-rose-50/40" : ""}`} data-testid={`prev-row-${v.attivita_id}`}>
                  <td className="p-2 font-semibold text-slate-800">{v.cantiere_nome}</td>
                  <td className="p-2 text-slate-600">{v.cliente || "—"}</td>
                  <td className="p-2">{v.fase_nome} {v.is_in_ritardo && <AlertTriangle className="inline w-3 h-3 text-rose-500 ml-1" />}</td>
                  <td className="p-2 font-mono text-xs">{v.data_prevista_fatturazione}</td>
                  <td className="p-2 text-right font-mono font-bold">{eur(v.importo_previsto)}</td>
                  <td className="p-2 text-right font-mono">{eur(v.importo_emesso)}</td>
                  <td className="p-2 text-right font-mono text-emerald-700">{eur(v.importo_incassato)}</td>
                  <td className="p-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${STATO_FASE_META[v.stato_fase]?.c || "bg-slate-100"}`}>
                      {STATO_FASE_META[v.stato_fase]?.l || v.stato_fase}
                    </span>
                  </td>
                  <td className={`p-2 text-center text-[11px] font-semibold ${STATO_RICH_META[v.richiesta_stato]?.c || "text-slate-400"}`}>
                    {STATO_RICH_META[v.richiesta_stato]?.l || "—"}
                  </td>
                </tr>
              ))}
              {!isLoading && voci.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-xs text-slate-400">Nessuna fase corrisponde ai filtri.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, highlight, icon }) {
  const bg = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  }[highlight] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`p-3 border rounded-sm ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70 flex items-center gap-1">{icon}{label}</div>
      <div className="font-display font-black text-xl mt-1 truncate">{value}</div>
    </div>
  );
}
